import { SerializedConstructor } from '@langchain/core/load/serializable';
import { Chip, FormLabel } from '@mui/joy';

import { useState } from 'react';

import { getModelDisplayNameFromIdList } from '@/Pages/Playground/Playground.constants';
import AnthropicLogo from '@/icons/AnthropicLogo.svg?react';
import { EPromptType } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import {
  ManifestEditorProps,
  ManifestPreviewProps,
  tableVariantContainerClass,
} from '../../Manifest.utils';
import { ManifestNavigation } from '../../ManifestNavigation';
import { ManifestSelectModel } from '../../ManifestSelectModel';
import { ManifestProviderReplace } from '../../ManifestSelectProvider';
import { ManifestSlider } from '../../ManifestSlider';
import { ManifestStopEditor } from '../../ManifestStopEditor';
import { ManifestChatAnthropicToolListEditor } from '../common/ManifestCommonChatAnthropic';
import { DEFAULT_MODEL, MODELS } from './ManifestChatAnthropic.utils';

export function ManifestChatAnthropic(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const kwargs = props.value.kwargs;
  const [topPEnabled, setTopPEnabled] = useState(kwargs.top_p != null);
  const [topKEnabled, setTopKEnabled] = useState(kwargs.top_k != null);
  const [temperatureEnabled, setTemperatureEnabled] = useState(
    kwargs.temperature != null
  );
  const [maxTokensEnabled, setMaxTokensEnabled] = useState(
    kwargs.max_tokens != null
  );

  const [localTopP, setLocalTopP] = useState(kwargs.top_p);
  const [localTopK, setLocalTopK] = useState(kwargs.top_k);
  const [localTemperature, setLocalTemperature] = useState(kwargs.temperature);
  const [localMaxTokens, setLocalMaxTokens] = useState(kwargs.max_tokens);

  return (
    <>
      <ManifestNavigation navigation={props.navigation} />

      <div
        className={cn(
          'flex flex-col gap-3',
          props.variant === 'table' && tableVariantContainerClass
        )}
      >
        <ManifestProviderReplace
          variant={props.variant}
          promptType={EPromptType.CHAT}
          onChange={props.onChange}
          value={props.value}
          readOnly={props.readOnly}
        />

        <ManifestSelectModel
          models={MODELS}
          onChange={(model) => {
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, model },
            });
          }}
          value={kwargs.model ?? DEFAULT_MODEL}
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Temperature"
          value={localTemperature}
          min={0}
          max={1}
          step={0.01}
          onChange={(temperature) => {
            setLocalTemperature(temperature);
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, temperature },
            });
          }}
          readOnly={props.readOnly}
          variant={props.variant}
          inputPosition={'right'}
          checked={temperatureEnabled}
          onCheckChange={(checked) => {
            setTemperatureEnabled(checked);
            if (!checked) {
              props.onChange({
                ...props.value,
                kwargs: { ...kwargs, temperature: null },
              });
            }
          }}
        />

        <div className="pt-[2px]">
          <ManifestSlider
            title="Max Output Tokens"
            value={localMaxTokens}
            min={1}
            max={64000}
            step={1}
            defaultValue={1024}
            onChange={(max_tokens) => {
              setLocalMaxTokens(max_tokens);
              props.onChange({
                ...props.value,
                kwargs: { ...kwargs, max_tokens },
              });
            }}
            readOnly={props.readOnly}
            variant={props.variant}
            inputPosition={'right'}
            checked={maxTokensEnabled}
            onCheckChange={(checked) => {
              setMaxTokensEnabled(checked);
              if (!checked) {
                props.onChange({
                  ...props.value,
                  kwargs: { ...kwargs, max_tokens: null },
                });
              }
            }}
          />
        </div>

        <div className="pt-[2px]">
          <ManifestSlider
            title="Top P"
            value={localTopP}
            min={0}
            max={1}
            step={0.01}
            onChange={(top_p) => {
              setLocalTopP(top_p);
              props.onChange({
                ...props.value,
                kwargs: { ...kwargs, top_p },
              });
            }}
            readOnly={props.readOnly}
            variant={props.variant}
            inputPosition={'right'}
            checked={topPEnabled}
            onCheckChange={(checked) => {
              setTopPEnabled(checked);
              if (!checked) {
                props.onChange({
                  ...props.value,
                  kwargs: { ...kwargs, top_p: null },
                });
              }
            }}
          />
        </div>

        <div className="pt-[2px]">
          <ManifestSlider
            title="Top K"
            value={localTopK}
            min={0}
            max={10000}
            step={1}
            onChange={(top_k) => {
              setLocalTopK(top_k);
              props.onChange({
                ...props.value,
                kwargs: {
                  ...kwargs,
                  top_k,
                },
              });
            }}
            readOnly={props.readOnly}
            variant={props.variant}
            inputPosition={'right'}
            checked={topKEnabled}
            onCheckChange={(checked) => {
              setTopKEnabled(checked);
              if (!checked) {
                props.onChange({
                  ...props.value,
                  kwargs: { ...kwargs, top_k: null },
                });
              }
            }}
          />
        </div>
        {!props.hideTools && <ManifestChatAnthropicToolListEditor {...props} />}

        {props.options && (!props.readOnly || !!props.options.stop) && (
          <div className="flex flex-col gap-2">
            <FormLabel>Stop Sequences</FormLabel>
            <ManifestStopEditor
              value={props.options.stop}
              onChange={(stop) =>
                props.onOptionsChange({
                  ...props.options,
                  stop,
                })
              }
              readOnly={props.readOnly}
            />
          </div>
        )}
      </div>
    </>
  );
}

export function ManifestChatAnthropicPreview(
  props: ManifestPreviewProps<SerializedConstructor>
) {
  const displayLabel = getModelDisplayNameFromIdList(props.value.id);
  const kwargs = props.value.kwargs;
  const model = kwargs.model ?? DEFAULT_MODEL;

  if (props.variant === 'line') {
    return (
      <div className="flex flex-row items-center gap-1">
        <span className="font-semibold">{displayLabel}</span>
        <span>{model}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        'flex w-auto select-none flex-col rounded-md bg-neutral-50 p-4 shadow-sm dark:bg-[rgb(9,9,13)]',
        props.onClick ? 'cursor-pointer' : 'cursor-default'
      )}
    >
      <span className="flex items-center gap-2">
        <AnthropicLogo />
        <span className="font-semibold">{displayLabel}</span>
      </span>

      <span className="mt-3 flex items-center gap-1">
        <Chip variant="outlined" size="sm" color="neutral">
          {model}
        </Chip>

        {kwargs.temperature != null && (
          <Chip variant="outlined" size="sm" color="neutral">
            Temperature: {kwargs.temperature}
          </Chip>
        )}

        {kwargs.max_tokens_to_sample != null && (
          <Chip variant="outlined" size="sm" color="neutral">
            Max Tokens to sample: {kwargs.max_tokens_to_sample}
          </Chip>
        )}
      </span>
    </button>
  );
}
